import useGetRequest from 'hooks/useGetRequest';
import { APIError } from 'lib/_api-helpers';
import { BuildMethod } from 'models/Activity';
import { Course } from 'models/Course';
import { ModuleItem } from 'pages/build-course/components/generateContentComponents/ModuleItem';
import * as React from 'react';
import { useParams } from 'react-router-dom';

type DataToShowGenerateContentModal = {
    activityId: string;
    buildMethod: BuildMethod;
};

type BuildCourseContextValue = {
    course: Course | undefined;
    snapshot: Course | undefined;
    setSnapshot: React.Dispatch<React.SetStateAction<Course | undefined>>;
    setCourseState: React.Dispatch<React.SetStateAction<Course | undefined>>;
    editMode: boolean;
    errors: APIError[];
    generateContentStatusesState: ModuleItem[];
    refetchGenerateContentStatuses: () => Promise<void>;
    showIncompleteStepsWarning: boolean;
    setShowIncompleteStepsWarning: React.Dispatch<React.SetStateAction<boolean>>;
    refetchCourse: () => Promise<void>;
    dataToShowGenerateContentModal: DataToShowGenerateContentModal | undefined;
    setDataToShowGenerateContentModal: React.Dispatch<
        React.SetStateAction<DataToShowGenerateContentModal | undefined>
    >;
    activeStep: StepId;
    setActiveStep: React.Dispatch<React.SetStateAction<StepId>>;
    editActivityId: string;
    setEditActivityId: React.Dispatch<React.SetStateAction<string>>;
};

export const BuildCourseContext = React.createContext<BuildCourseContextValue>({
    course: undefined,
    snapshot: undefined,
    setSnapshot: () => {},
    setCourseState: () => {},
    refetchCourse: async () => {},
    showIncompleteStepsWarning: false,
    setShowIncompleteStepsWarning: () => {},
    errors: [],
    generateContentStatusesState: [],
    refetchGenerateContentStatuses: async () => {},
    dataToShowGenerateContentModal: undefined,
    setDataToShowGenerateContentModal: () => {},
    editMode: false,
    activeStep: 'courseDetails',
    setActiveStep: () => {},
    editActivityId: '',
    setEditActivityId: () => {},
});

type BuildCourseProviderProps = {
    children: React.ReactNode | React.ReactNode[];
    editMode: boolean;
};

export type StepId =
    | 'courseDetails'
    | 'courseLearningObjectives'
    | 'courseStructureAndContent'
    | 'courseAIGeneratedContent';

export function BuildCourseProvider({ children, editMode }: BuildCourseProviderProps): JSX.Element {
    const params = useParams();
    const { courseId } = params;
    const { data: course, errors, refetchData } = useGetRequest<Course>(`courses/${courseId}`, !courseId);

    const { data: generateContentStatuses, refetchData: refetchGenerateContentStatuses } = useGetRequest<ModuleItem[]>(
        `courses/${courseId}/generate-content-statuses`,
        !course,
    );

    const [courseState, setCourseState] = React.useState<Course>();
    const [snapshot, setSnapshot] = React.useState<Course>();
    const [generateContentStatusesState, setGenerateContentStatusesState] = React.useState<ModuleItem[]>();
    const [showIncompleteStepsWarning, setShowIncompleteStepsWarning] = React.useState<boolean>(false);
    const [dataToShowGenerateContentModal, setDataToShowGenerateContentModal] =
        React.useState<DataToShowGenerateContentModal>();
    const [activeStep, setActiveStep] = React.useState<StepId>('courseDetails');
    const [editActivityId, setEditActivityId] = React.useState<string>('');

    React.useEffect(() => {
        if (course) {
            setCourseState(course);
            setSnapshot((prev) => prev ?? structuredClone(course));
        }
    }, [course]);

    React.useEffect(() => {
        if (courseState) {
            const fetchData = async () => {
                await refetchGenerateContentStatuses();
            };

            fetchData();
        }
    }, [courseState]); //eslint-disable-line

    React.useEffect(() => {
        if (!!generateContentStatuses) {
            setGenerateContentStatusesState(generateContentStatuses);
        }
    }, [generateContentStatuses]);

    if (!courseState) return <></>;
    if (!generateContentStatusesState) return <></>;

    return (
        <BuildCourseContext.Provider
            value={{
                course: courseState,
                snapshot: snapshot,
                setSnapshot: setSnapshot,
                setCourseState: setCourseState,
                showIncompleteStepsWarning: showIncompleteStepsWarning,
                setShowIncompleteStepsWarning: setShowIncompleteStepsWarning,
                refetchCourse: refetchData,
                errors: errors,
                generateContentStatusesState: generateContentStatusesState,
                refetchGenerateContentStatuses: refetchGenerateContentStatuses,
                dataToShowGenerateContentModal: dataToShowGenerateContentModal,
                setDataToShowGenerateContentModal: setDataToShowGenerateContentModal,
                editMode: editMode,
                activeStep: activeStep,
                setActiveStep: setActiveStep,
                editActivityId: editActivityId,
                setEditActivityId: setEditActivityId,
            }}
        >
            {children}
        </BuildCourseContext.Provider>
    );
}
